import { useMemo, useState } from 'react'
import { ArrowLeft, MoonStar, Sun } from 'lucide-react'
import { Link, Outlet } from 'react-router-dom'
import { brand as bmcBrand } from '@/ui/brand'
import { useAuth } from '../contexts/AuthContext'
import { useTheme } from '../contexts/ThemeContext'
import { EditSessionProvider, type EditSession } from '../contexts/EditSessionContext'
import { AdminUserMenu } from './admin/AdminUserMenu'

/**
 * Schlanke vCard-Editor-Shell (BMC-Designsystem `console.css`): fokussierter
 * `.shell`-Header (BMC-Logo + Kontext „vCard bearbeiten", Theme-Umschalter,
 * UserMenu) über einem einspaltigen `.canvas`/`.wrap-narrow` (`<Outlet/>`).
 * Keine Verwaltungs-Nav.
 *
 * Edit-on-behalf: Bearbeitet admin/brand-admin eine *fremde* vCard, registriert
 * die Seite (`useRegisterEditSession`) den Zielnutzer + das Rücksprungziel; das
 * Layout zeigt dann den Kontext-Banner `.ctxbar` „Du bearbeitest die vCard von
 * <X>" samt „Zurück zur Übersicht". Bei der *eigenen* vCard (`/editor/me`) wird
 * nichts registriert → kein Banner/kein Zurück.
 */
export function EditorLayout() {
  const { session } = useAuth()
  const { theme, toggle } = useTheme()
  const [editSession, setEditSession] = useState<EditSession | null>(null)
  const registry = useMemo(() => ({ register: setEditSession }), [])

  if (!session) return null // durch RequireAuth abgesichert

  const logo = theme === 'dark' ? bmcBrand.logoDark : bmcBrand.logoLight

  return (
    <EditSessionProvider value={registry}>
      <div className="app">
        <header className="shell">
          <div className="wordmark">
            <img src={logo} alt={bmcBrand.name} className="brand-logo" />
            <span className="name">
              {bmcBrand.product} <span className="suffix">· vCard bearbeiten</span>
            </span>
          </div>
          <span className="spacer" />
          <button
            type="button"
            className="icon-btn"
            aria-label="Design wechseln"
            aria-pressed={theme === 'dark'}
            onClick={toggle}
          >
            {theme === 'dark' ? <Sun size={18} /> : <MoonStar size={18} />}
          </button>
          <AdminUserMenu session={session} />
        </header>

        <main className="canvas">
          <div className="wrap-wide">
            {editSession && (
              <div className="ctxbar">
                <span className="cb-strong">Edit-on-behalf</span>
                <span>
                  Du bearbeitest die vCard von{' '}
                  <span className="cb-strong">{editSession.targetLabel}</span>
                </span>
                <span className="spacer" />
                <Link to={editSession.backTo} className="btn ghost sm">
                  <ArrowLeft size={16} />
                  Zurück zur Übersicht
                </Link>
              </div>
            )}
            <Outlet />
          </div>
        </main>
      </div>
    </EditSessionProvider>
  )
}
